/**
 * Ray Tracing Signal Propagation Algorithm
 *
 * This algorithm simulates signal propagation using ray tracing techniques.
 * It models how wireless signals interact with walls and obstacles in the home.
 * It iteratively determines the number of units needed for Basic, Medium, and High coverage targets,
 * keeping the primary router location fixed and using physics-based simulations.
 */
import { WIRELESS_SPECS } from '../constants';
// Import necessary physics/simulation helpers from logic.js
import {
    getMaterialPenetrationLoss,
    createCoverageGrid, // Use the one from logic.js
    simulateCoverageGridForFrequency,
    mapFrequenciesToSpeedTiers,
    getIntersectionCorrected // Import the corrected intersection checker
} from '../logic';

// Constants
const MAX_UNITS_CAP = 6; // Ray tracing is expensive, keep cap lower
const TARGET_COVERAGE_PERCENT = 99; // Target coverage percentage
const GRID_STEP = 10; // Use consistent grid step from logic.js
// Define signal strength thresholds (dBm) for coverage levels (used by mapFrequenciesToSpeedTiers)
const HIGH_SENSITIVITY_THRESHOLD = -75;
const MEDIUM_SENSITIVITY_THRESHOLD = -80;
const BASIC_SENSITIVITY_THRESHOLD = -85;
const MIN_DISTANCE_BETWEEN_UNITS_METERS = 5; // Min distance in meters


// --- Helper Functions ---

// **Modified**: Helper to determine primary router coordinates using floorPlanLayout dimensions
const getPrimaryRouterLocation = (formData, floorPlanLayout) => {
  // Start at center in meters using layout dimensions
  const widthMeters = floorPlanLayout.layoutWidthMeters;
  const heightMeters = floorPlanLayout.layoutHeightMeters;
  let xMeters = widthMeters / 2;
  let yMeters = heightMeters / 2;

  // Adjust based on location preference (relative adjustments)
  if (formData.routerLocation === 'livingRoom') { xMeters *= 1.2; yMeters *= 0.8; }
  else if (formData.routerLocation === 'office') { xMeters *= 0.6; yMeters *= 0.6; }
  else if (formData.routerLocation === 'bedroom') { xMeters *= 1.4; yMeters *= 0.6; }
  else if (formData.routerLocation === 'kitchen') { xMeters *= 0.6; yMeters *= 1.4; }
  // Add other locations if needed

  // Clamp to layout bounds (in meters)
  return {
      x: Math.max(0, Math.min(widthMeters, xMeters)),
      y: Math.max(0, Math.min(heightMeters, yMeters))
  };
};

const getPrimaryRouterPlacementDescription = (formData) => {
  if (formData.routerLocation === 'livingRoom') return 'Place your primary router in the living room, preferably in a central location and elevated.';
  if (formData.routerLocation === 'office') return 'Place your primary router in the home office, preferably elevated and away from other electronics.';
  if (formData.routerLocation === 'bedroom') return 'Place your primary router in the bedroom, preferably elevated and away from the bed.';
  if (formData.routerLocation === 'kitchen') return 'Place your primary router in the kitchen, but keep it away from appliances like microwaves that can cause interference.';
  if (formData.routerLocation === 'hallway') return 'Place your primary router in the hallway, which provides good central coverage for the surrounding rooms.';
  if (formData.routerLocation === 'basement') return 'Place your primary router in the basement, but be aware that this may limit coverage on upper floors.';
  if (formData.routerLocation === 'custom' && formData.customRouterLocation) return `Place your primary router in ${formData.customRouterLocation}, preferably elevated for better signal propagation.`;
  return 'Place your primary router in a central location in your home, preferably elevated for better signal propagation.';
};

// Removed getFloorPlanDimensions - calculate dimensions directly

// **Removed**: createHomeModel and createObstaclesFromHomeModel - Use floorPlanLayout


// **Modified**: Generate potential positions using floorPlanLayout dimensions and rooms
const generatePotentialPositions = (floorPlanLayout) => {
  const widthMeters = floorPlanLayout.layoutWidthMeters;
  const heightMeters = floorPlanLayout.layoutHeightMeters;
  const rooms = floorPlanLayout.rooms; // Use rooms from layout
  const positions = [];
  const gridStepMeters = 3; // Place potential points every 3 meters

  // Grid points
  for (let x = gridStepMeters; x < widthMeters; x += gridStepMeters) {
      for (let y = gridStepMeters; y < heightMeters; y += gridStepMeters) {
          positions.push({ x, y });
      }
  }
  // Room centers (using room data from floorPlanLayout)
  rooms.forEach(r => {
      positions.push({ x: r.x + r.width / 2, y: r.y + r.height / 2 });
  });

  // Ensure uniqueness (rounding slightly to avoid floating point issues)
  const unique = [];
  const seen = new Set();
  positions.forEach(p => {
      const k = `${Math.round(p.x * 10) / 10},${Math.round(p.y * 10) / 10}`; // Key based on meters rounded to 1 decimal
      if (!seen.has(k)) {
          // Clamp positions to be within layout bounds
          const clampedX = Math.max(0, Math.min(widthMeters, p.x));
          const clampedY = Math.max(0, Math.min(heightMeters, p.y));
          unique.push({ x: clampedX, y: clampedY });
          seen.add(k);
      }
  });
  return unique;
};

// getRouterRange is less critical now, FSPL dominates. Keep as is for now.
const getRouterRange = (specs) => { return specs?.RANGE || 15; };

// **Modified**: castRay uses floorPlanLayout obstacles and formData factors
const castRay = (pos, angle, floorPlanLayout, formData, rangeMeters) => {
  const dx = Math.cos(angle); const dy = Math.sin(angle);
  const x1 = pos.x; const y1 = pos.y; // Already in meters
  let closestDist = rangeMeters; // Range is now in meters

  // Use factors from formData
  const wallDensityFactor = WIRELESS_SPECS.WALL_DENSITY_FACTORS[formData.wallDensity] || 0.7;
  const constructionTypeFactor = WIRELESS_SPECS.CONSTRUCTION_TYPE_FACTORS[formData.constructionType] || 0.7;
  const wallPenaltyFactor = (1.0 - (wallDensityFactor * constructionTypeFactor * 0.7));

  let wallsHit = 0;
  // Find the closest wall intersection using layout obstacles (meters)
  for (const wall of floorPlanLayout.obstacles) {
      const intersection = getIntersectionCorrected(x1, y1, x1 + dx * rangeMeters, y1 + dy * rangeMeters, wall.x1, wall.y1, wall.x2, wall.y2);
      if (intersection) {
          // Distance is calculated in meters
          const distToIntersection = Math.sqrt(Math.pow(x1 - intersection.x, 2) + Math.pow(y1 - intersection.y, 2));
          if (distToIntersection < closestDist) {
              closestDist = distToIntersection;
          }
          wallsHit++;
      }
  }

  // Attenuated range in meters
  const attenuatedRange = rangeMeters * Math.pow(wallPenaltyFactor, wallsHit);

  // Ray length in meters
  const rayLength = Math.min(closestDist, attenuatedRange);

  return Math.max(0, rayLength);
};


// **Modified**: calculateCoverageScore uses floorPlanLayout and formData
const calculateCoverageScore = (pos, floorPlanLayout, specs, formData) => {
    let score = 0;
    const rayCount = 36;
    const angleStep = (2 * Math.PI) / rayCount;
    const rangeMeters = getRouterRange(specs); // Use meter range if needed

    for (let i = 0; i < rayCount; i++) {
        // Pass meter-based position, layout, formData, and range
        score += castRay(pos, i * angleStep, floorPlanLayout, formData, rangeMeters);
    }
    // Normalize score based on meter range
    return score / (rayCount * rangeMeters);
};

// **Modified**: evaluatePositions uses floorPlanLayout and formData
const evaluatePositions = (potentialPositions, floorPlanLayout, specs, formData) => {
    // potentialPositions are already in meters
    return potentialPositions.map(p => ({ ...p, score: calculateCoverageScore(p, floorPlanLayout, specs, formData) }));
};

// **Modified**: selectNextBestMeshPosition uses meter-based minDistance
const selectNextBestMeshPosition = (evaluatedPositions, existingPositions, minDistanceMeters = MIN_DISTANCE_BETWEEN_UNITS_METERS) => {
    const sortedPotential = [...evaluatedPositions]
        .sort((a, b) => b.score - a.score); // Best score first

    for (const potentialPos of sortedPotential) { // potentialPos coords are in meters
        let isFarEnough = true;
        const allExisting = existingPositions; // existingPositions coords are in meters
        for (const existingPos of allExisting) {
            // Calculate distance in meters
            const dist = Math.sqrt(Math.pow(potentialPos.x - existingPos.x, 2) + Math.pow(potentialPos.y - existingPos.y, 2));
            if (dist < minDistanceMeters) { // Compare with meter distance
                isFarEnough = false;
                break;
            }
        }
        if (isFarEnough) {
             // Check if position already exists (using meter coords)
            if (!allExisting.some(p => p.x === potentialPos.x && p.y === potentialPos.y)) {
                 return potentialPos;
            }
        }
    }
    // Fallback
    const existingCoords = new Set(existingPositions.map(p => `${p.x},${p.y}`));
    return sortedPotential.find(p => !existingCoords.has(`${p.x},${p.y}`)) || null;
};


// **Modified**: formatSolutionPlacements uses meter-based positions
const formatSolutionPlacements = (positions, formData) => {
  // Positions are already in meters
  return positions.map((pos, index) => ({
    type: pos.type,
    position: { x: pos.x, y: pos.y }, // Store meter coordinates
    description: pos.type === 'primary' ? getPrimaryRouterPlacementDescription(formData) : `Place mesh unit ${index + 1} (Ray Tracing). Score: ${pos.score?.toFixed(2) ?? 'N/A'}`,
    tips: []
  }));
};

// --- Exported Functions ---

/**
 * Runs the Ray Tracing algorithm to find the best placement for a GIVEN number of units,
 * keeping the primary router fixed. Exported for recalculation purposes.
 * Uses floorPlanLayout.
 */
export const calculateRayTracingPlacementsForCount = (formData, routerStandard, routerSpecs, unitCount, floorPlanLayout) => {
  if (unitCount < 1 || !floorPlanLayout) return []; // Check for layout
  unitCount = Math.min(unitCount, MAX_UNITS_CAP);

  // Use dimensions, rooms, and obstacles from floorPlanLayout
  const widthMeters = floorPlanLayout.layoutWidthMeters;
  const heightMeters = floorPlanLayout.layoutHeightMeters;
  // const obstacles = floorPlanLayout.obstacles; // Obstacles used in simulation

  // Determine primary location using layout dimensions
  const primaryPos = getPrimaryRouterLocation(formData, floorPlanLayout); // Meter coords
  // Generate potential positions using layout
  const potentialPositions = generatePotentialPositions(floorPlanLayout); // Meter coords
  // Evaluate positions using layout and formData
  const evaluatedPositions = evaluatePositions(potentialPositions, floorPlanLayout, routerSpecs, formData); // Meter coords

  // Start with primary position (meters)
  const selectedPositionsCoords = [{ x: primaryPos.x, y: primaryPos.y }];
  const finalPlacements = [{ ...primaryPos, type: 'primary' }]; // Includes meter coords

  // Iteratively select best mesh positions (meters)
  while (finalPlacements.length < unitCount) {
      const nextBest = selectNextBestMeshPosition(evaluatedPositions, selectedPositionsCoords); // Uses meter distance
      if (nextBest) { // nextBest has meter coords
          finalPlacements.push({ ...nextBest, type: 'mesh' });
          selectedPositionsCoords.push({ x: nextBest.x, y: nextBest.y });
      } else {
          console.warn("RayTracing: Could not find enough valid positions.");
          break;
      }
  }

  return formatSolutionPlacements(finalPlacements, formData); // Formats meter coords
};

/**
 * Calculates coverage percentages based on placements using physics simulation
 * and the provided floorPlanLayout.
 */
export const calculateRayTracingCoveragePercentage = (unitCount, placements, formData, routerSpecs, floorPlanLayout) => {
    if (!floorPlanLayout || !placements || placements.length === 0) {
        return { basic: 0, medium: 0, high: 0 };
    }
    // Use dimensions and obstacles from floorPlanLayout
    const widthMeters = floorPlanLayout.layoutWidthMeters;
    const heightMeters = floorPlanLayout.layoutHeightMeters;
    const obstacles = floorPlanLayout.obstacles; // Meter coords

    // Extract positions (already in meters from placements)
    const positions = placements.map(p => ({...p.position, type: p.type})); // Meter coords

    // Create grids using area (or layout dimensions if createCoverageGrid supports it)
    const aspectRatio = widthMeters / heightMeters || 1;
    const grid2_4 = createCoverageGrid(formData.area, aspectRatio);
    const grid5 = createCoverageGrid(formData.area, aspectRatio);
    const grid6 = (routerSpecs.BANDS?.includes('6GHz')) ? createCoverageGrid(formData.area, aspectRatio) : null;

    // Define frequency parameters
    const params2_4 = { frequencyGHz: 2.4, transmitPowerDBm: routerSpecs.TX_POWER_2_4GHZ || 20, penetrationLossDB: getMaterialPenetrationLoss(2.4, formData.wallDensity, formData.constructionType) };
    const params5 = { frequencyGHz: 5, transmitPowerDBm: routerSpecs.TX_POWER_5GHZ || 23, penetrationLossDB: getMaterialPenetrationLoss(5, formData.wallDensity, formData.constructionType) };
    const params6 = grid6 ? { frequencyGHz: 6, transmitPowerDBm: routerSpecs.TX_POWER_6GHZ || 23, penetrationLossDB: getMaterialPenetrationLoss(6, formData.wallDensity, formData.constructionType) } : null;

    // Simulate coverage using meter-based positions and layout obstacles
    simulateCoverageGridForFrequency(positions, grid2_4, obstacles, params2_4);
    simulateCoverageGridForFrequency(positions, grid5, obstacles, params5);
    if (grid6 && params6) {
        simulateCoverageGridForFrequency(positions, grid6, obstacles, params6);
    }

    // Map frequencies using the updated grids
    return mapFrequenciesToSpeedTiers(grid2_4, grid5, grid6, routerSpecs);
};


/**
 * Main function for the Ray Tracing Algorithm.
 * Iteratively determines the number of units required to meet coverage targets.
 * Uses the provided floorPlanLayout.
 */
const rayTracingAlgorithm = (formData, routerStandard, routerSpecs, floorPlanLayout) => {
  // Check if floorPlanLayout is provided
  if (!floorPlanLayout) {
      console.error("Ray Tracing Algorithm requires floorPlanLayout.");
      // Return a default error structure or throw an error
      return {
          coverageLevels: { basic: { units: 0, placements: [] }, medium: { units: 0, placements: [] }, high: { units: 0, placements: [] } },
          coveragePercentages: { basic: 0, medium: 0, high: 0 }
      };
  }

  // Use dimensions, obstacles, and rooms from floorPlanLayout
  const widthMeters = floorPlanLayout.layoutWidthMeters;
  const heightMeters = floorPlanLayout.layoutHeightMeters;
  const obstacles = floorPlanLayout.obstacles; // Use layout obstacles
  const potentialPositions = generatePotentialPositions(floorPlanLayout); // Meter coords
  const evaluatedPositions = evaluatePositions(potentialPositions, floorPlanLayout, routerSpecs, formData); // Meter coords

  // Determine primary router location using layout dimensions
  const primaryPos = getPrimaryRouterLocation(formData, floorPlanLayout); // Meter coords
  const fixedPrimaryPlacement = { ...primaryPos, type: 'primary' }; // Meter coords

  let currentUnitCount = 1;
  let currentPlacements = [fixedPrimaryPlacement]; // Meter coords
  let currentPercentages = { basic: 0, medium: 0, high: 0 };

  let basicResult = null;
  let mediumResult = null;
  let highResult = null;

  console.log(`RayTracing: Starting iterative calculation (using meters)...`);

   // Define frequency parameters once
  const params2_4 = { frequencyGHz: 2.4, transmitPowerDBm: routerSpecs.TX_POWER_2_4GHZ || 20, penetrationLossDB: getMaterialPenetrationLoss(2.4, formData.wallDensity, formData.constructionType) };
  const params5 = { frequencyGHz: 5, transmitPowerDBm: routerSpecs.TX_POWER_5GHZ || 23, penetrationLossDB: getMaterialPenetrationLoss(5, formData.wallDensity, formData.constructionType) };
  const params6 = (routerSpecs.BANDS?.includes('6GHz')) ? { frequencyGHz: 6, transmitPowerDBm: routerSpecs.TX_POWER_6GHZ || 23, penetrationLossDB: getMaterialPenetrationLoss(6, formData.wallDensity, formData.constructionType) } : null;

  // Create grids using area (or layout dimensions if createCoverageGrid supports it)
  const aspectRatio = widthMeters / heightMeters || 1;
  const grid2_4 = createCoverageGrid(formData.area, aspectRatio);
  const grid5 = createCoverageGrid(formData.area, aspectRatio);
  const grid6 = params6 ? createCoverageGrid(formData.area, aspectRatio) : null;


  while (currentUnitCount <= MAX_UNITS_CAP) {
    console.log(`RayTracing: Calculating for ${currentUnitCount} units...`);

    // Simulate coverage using meter-based placements, obstacles, and the appropriate grid
    // Note: simulateCoverageGridForFrequency modifies the grid object directly
    const currentGrid2_4 = simulateCoverageGridForFrequency([...currentPlacements], { ...grid2_4, cells: grid2_4.cells.map(c => ({...c, signal: -Infinity})) }, obstacles, params2_4); // Reset signals before sim
    const currentGrid5 = simulateCoverageGridForFrequency([...currentPlacements], { ...grid5, cells: grid5.cells.map(c => ({...c, signal: -Infinity})) }, obstacles, params5); // Reset signals before sim
    let currentGrid6 = null;
    if (grid6 && params6) {
        currentGrid6 = simulateCoverageGridForFrequency([...currentPlacements], { ...grid6, cells: grid6.cells.map(c => ({...c, signal: -Infinity})) }, obstacles, params6); // Reset signals before sim
    }


    // Map to speed tier percentages using the results of the simulation for this unit count
    // Pass floorPlanLayout to percentage calculation (though it might not use it directly)
    currentPercentages = calculateRayTracingCoveragePercentage(currentUnitCount, currentPlacements, formData, routerSpecs, floorPlanLayout);
    // currentPercentages = mapFrequenciesToSpeedTiers(currentGrid2_4, currentGrid5, currentGrid6, routerSpecs); // Old way
    console.log(`RayTracing: ${currentUnitCount} units -> B:${currentPercentages.basic}%, M:${currentPercentages.medium}%, H:${currentPercentages.high}%`);

    // Check if targets met and store the first time they are met
    if (!basicResult && currentPercentages.basic >= TARGET_COVERAGE_PERCENT) {
      console.log(`RayTracing: Basic target met at ${currentUnitCount} units.`);
      // Store placements (meter coords) and percentages for this count
      basicResult = { units: currentUnitCount, placements: formatSolutionPlacements([...currentPlacements], formData), coverage: {...currentPercentages} };
    }
    if (!mediumResult && currentPercentages.medium >= TARGET_COVERAGE_PERCENT) {
      console.log(`RayTracing: Medium target met at ${currentUnitCount} units.`);
      mediumResult = { units: currentUnitCount, placements: formatSolutionPlacements([...currentPlacements], formData), coverage: {...currentPercentages} };
    }
    if (!highResult && currentPercentages.high >= TARGET_COVERAGE_PERCENT) {
      console.log(`RayTracing: High target met at ${currentUnitCount} units.`);
      highResult = { units: currentUnitCount, placements: formatSolutionPlacements([...currentPlacements], formData), coverage: {...currentPercentages} };
    }

    // Exit loop if all targets are met OR max units reached
    if ((basicResult && mediumResult && highResult) || currentUnitCount >= MAX_UNITS_CAP) {
       console.log(`RayTracing: Stopping iteration (Targets Met: ${!!(basicResult && mediumResult && highResult)}, Max Units: ${currentUnitCount >= MAX_UNITS_CAP})`);
       break;
    }

    // If targets not met, find the *next best* mesh position to add (using meter coords)
    const currentCoords = currentPlacements.map(p => ({ x: p.x, y: p.y })); // Meter coords
    const nextBestMeshPos = selectNextBestMeshPosition(evaluatedPositions, currentCoords); // Uses meter distance

    if (nextBestMeshPos) { // nextBestMeshPos has meter coords
         currentPlacements.push({ ...nextBestMeshPos, type: 'mesh' });
    } else {
        console.log(`RayTracing: Could not find suitable next mesh position. Stopping.`);
        break;
    }
    // Increment count for the next loop iteration
    currentUnitCount++;

  } // End while loop


   // If targets weren't met after reaching the cap, use the results from the max unit count achieved
   // Recalculate percentages for the final state if loop exited due to MAX_UNITS_CAP and targets weren't met
   let finalPercentages = currentPercentages; // Use last calculated percentages if targets met or no recalc needed
   if (!(basicResult && mediumResult && highResult)) {
        console.log(`RayTracing: Recalculating final percentages for ${currentPlacements.length} units.`);
        const finalGrid2_4 = simulateCoverageGridForFrequency([...currentPlacements], { ...grid2_4, cells: grid2_4.cells.map(c => ({...c, signal: -Infinity})) }, obstacles, params2_4);
        const finalGrid5 = simulateCoverageGridForFrequency([...currentPlacements], { ...grid5, cells: grid5.cells.map(c => ({...c, signal: -Infinity})) }, obstacles, params5);
        let finalGrid6 = null;
        if (grid6 && params6) {
            finalGrid6 = simulateCoverageGridForFrequency([...currentPlacements], { ...grid6, cells: grid6.cells.map(c => ({...c, signal: -Infinity})) }, obstacles, params6);
        }
        finalPercentages = mapFrequenciesToSpeedTiers(finalGrid2_4, finalGrid5, finalGrid6, routerSpecs);
   }
   // Use the placements from the last successful iteration
   const finalPlacementsFormatted = formatSolutionPlacements([...currentPlacements], formData);
   const finalResult = { units: currentPlacements.length, placements: finalPlacementsFormatted, coverage: {...finalPercentages} };

   // Assign final result if specific targets weren't met
   if (!basicResult) { console.log(`RayTracing: Basic target not met, using max units (${finalResult.units}).`); basicResult = finalResult; }
   if (!mediumResult) { console.log(`RayTracing: Medium target not met, using max units (${finalResult.units}).`); mediumResult = finalResult; }
   if (!highResult) { console.log(`RayTracing: High target not met, using max units (${finalResult.units}).`); highResult = finalResult; }

  // Return results based on when each target was first met, or the final state if not met
  return {
    coverageLevels: {
      basic: { units: basicResult.units, placements: basicResult.placements },
      medium: { units: mediumResult.units, placements: mediumResult.placements },
      high: { units: highResult.units, placements: highResult.placements }
    },
    coveragePercentages: { // Report the coverage achieved *at the point the target was met* or the final coverage if not met
        basic: basicResult.coverage.basic,
        medium: mediumResult.coverage.medium,
        high: highResult.coverage.high
    }
  };
};

export default rayTracingAlgorithm;